import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { Suspense } from 'react'
import { AnimatePresence } from 'framer-motion'
import { HelmetProvider } from 'react-helmet-async'
import FullPageLoader from './components/FullPageLoader'
import ProtectedRoute from './components/ProtectedRoute'
import ScrollToTop from './components/ScrollToTop'
import * as Sentry from '@sentry/react'
import ErrorFallback from './components/ErrorFallback'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ThemeProvider } from './contexts/ThemeContext'
import { AuthProvider } from './contexts/AuthContext'
import { ActiveAnalysisProvider } from './contexts/ActiveAnalysisContext'
import { lazyWithRetry } from './lib/lazyWithRetry'
import { isPwaHost } from './lib/pwaHost'
import UpdateAvailableModal from './components/UpdateAvailableModal'
import PWAInstallPrompt from './components/PWAInstallPrompt'
import PostPaymentTour from './components/tour/PostPaymentTour'

// Auth funnel — eager-bundled so there's zero Suspense flash between
// the marketing site and Login/Signup. Premium Linear/Vercel/Mochi feel.
import Login from './pages/Login'
import Signup from './pages/Signup'

// Lazy load pages with auto-retry on stale chunks
const Landing = lazyWithRetry(() => import('./pages/Landing'))
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'))
const Analyze = lazyWithRetry(() => import('./pages/Analyze'))
const Result = lazyWithRetry(() => import('./pages/Result'))
const Admin = lazyWithRetry(() => import('./pages/Admin'))
const Onboarding = lazyWithRetry(() => import('./pages/Onboarding'))
const Profile = lazyWithRetry(() => import('./pages/Profile'))
const EmailConfirmed = lazyWithRetry(() => import('./pages/EmailConfirmed'))
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'))
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword'))
const Shop = lazyWithRetry(() => import('./pages/Shop'))
const PaymentSuccess = lazyWithRetry(() => import('./pages/PaymentSuccess'))
const PaymentCancel = lazyWithRetry(() => import('./pages/PaymentCancel'))
const Terms = lazyWithRetry(() => import('./pages/Terms'))
const Privacy = lazyWithRetry(() => import('./pages/Privacy'))
const CompareAnalyses = lazyWithRetry(() => import('./pages/CompareAnalyses'))
const CheckEmail = lazyWithRetry(() => import('./pages/CheckEmail'))
const Contact = lazyWithRetry(() => import('./pages/Contact'))
const Results = lazyWithRetry(() => import('./pages/Results'))
const CRM = lazyWithRetry(() => import('./pages/CRM'))
const Integrations = lazyWithRetry(() => import('./pages/Integrations'))
const IntegrationGuide = lazyWithRetry(() => import('./pages/IntegrationGuide'))
const Tutorials = lazyWithRetry(() => import('./pages/Tutorials'))
const EarlyBird = lazyWithRetry(() => import('./pages/EarlyBird'))

// BetterNotes (feature-flagged)
const BetterNotesLayout = lazyWithRetry(() => import('./components/betternotes/BetterNotesLayout'))
const BetterNotesLibrary = lazyWithRetry(() => import('./pages/betternotes/Library'))
const BetterNotesUpcoming = lazyWithRetry(() => import('./pages/betternotes/Upcoming'))
const BetterNotesPast = lazyWithRetry(() => import('./pages/betternotes/Past'))
const BetterNotesSettings = lazyWithRetry(() => import('./pages/betternotes/Settings'))
const BetterNotesCallDetail = lazyWithRetry(() => import('./pages/betternotes/CallDetail'))
const BetterNotesOAuthGoogleCallback = lazyWithRetry(() => import('./pages/betternotes/OAuthGoogleCallback'))
const BetterNotesOAuthOutlookCallback = lazyWithRetry(() => import('./pages/betternotes/OAuthOutlookCallback'))
const BetterNotesSharedRecording = lazyWithRetry(() => import('./pages/betternotes/SharedRecording'))

// Website (marketing) pages
const WebsiteLayout = lazyWithRetry(() => import('./components/website/WebsiteLayout'))
const Features = lazyWithRetry(() => import('./pages/Features'))
const PublicPricing = lazyWithRetry(() => import('./pages/PublicPricing'))
const About = lazyWithRetry(() => import('./pages/About'))
const Blog = lazyWithRetry(() => import('./pages/Blog'))
const BlogPost = lazyWithRetry(() => import('./pages/BlogPost'))
const NotFound = lazyWithRetry(() => import('./pages/NotFound'))

function AnimatedRoutes() {
  const location = useLocation()

  return (
      <Suspense fallback={<FullPageLoader />}>
        <ScrollToTop />
        {/* Global guided tour for paid users post-checkout. Mounted as a
            sibling to <Routes> so its index + spotlight survives route
            transitions (the tour is multi-page by design). The tour
            self-gates on subscription_tier + a pending flag set by
            PaymentSuccess.tsx, so on free / un-triggered users it
            renders nothing. */}
        <PostPaymentTour />
        <AnimatePresence mode="wait" initial={false}>
        <Routes location={location} key={location.pathname}>
        {/* Marketing pages — wrapped in WebsiteLayout (navbar + footer) */}
        <Route element={<WebsiteLayout />}>
          <Route path="/" element={<Landing />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<PublicPricing />} />
          <Route path="/about" element={<About />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Public routes (own layout) */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/email-confirmed" element={<EmailConfirmed />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/compare-analyses" element={<CompareAnalyses />} />
        <Route path="/check-email" element={<CheckEmail />} />
        <Route path="/100-premiers" element={<EarlyBird />} />
        <Route path="/bn/share/:token" element={<BetterNotesSharedRecording />} />

        {/* Onboarding route (semi-protected) */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute skipOnboardingCheck>
              <Onboarding />
            </ProtectedRoute>
          }
        />

        {/* Protected routes — with per-route error boundaries */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Sentry.ErrorBoundary fallback={<ErrorFallback page="dashboard" />}>
                <Dashboard />
              </Sentry.ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/analyze"
          element={
            <ProtectedRoute>
              <Sentry.ErrorBoundary fallback={<ErrorFallback page="analyze" />}>
                <Analyze />
              </Sentry.ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/result"
          element={
            <ProtectedRoute>
              <Sentry.ErrorBoundary fallback={<ErrorFallback page="result" />}>
                <Result />
              </Sentry.ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/results"
          element={
            <ProtectedRoute>
              <Sentry.ErrorBoundary fallback={<ErrorFallback page="results" />}>
                <Results />
              </Sentry.ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/crm"
          element={
            <ProtectedRoute>
              <Sentry.ErrorBoundary fallback={<ErrorFallback page="crm" />}>
                <CRM />
              </Sentry.ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations"
          element={
            <ProtectedRoute>
              <Sentry.ErrorBoundary fallback={<ErrorFallback page="integrations" />}>
                <Integrations />
              </Sentry.ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/integrations/guide/:platform"
          element={
            <ProtectedRoute>
              <Sentry.ErrorBoundary fallback={<ErrorFallback page="integrations" />}>
                <IntegrationGuide />
              </Sentry.ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/betternotes"
          element={
            <ProtectedRoute>
              <Sentry.ErrorBoundary fallback={<ErrorFallback page="betternotes" />}>
                <BetterNotesLayout />
              </Sentry.ErrorBoundary>
            </ProtectedRoute>
          }
        >
          <Route index element={<BetterNotesLibrary />} />
          <Route path="upcoming" element={<BetterNotesUpcoming />} />
          <Route path="past" element={<BetterNotesPast />} />
          <Route path="settings" element={<BetterNotesSettings />} />
          <Route path="recording/:id" element={<BetterNotesCallDetail />} />
          <Route path="oauth/google/callback" element={<BetterNotesOAuthGoogleCallback />} />
          <Route path="oauth/outlook/callback" element={<BetterNotesOAuthOutlookCallback />} />
        </Route>
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tutorials"
          element={
            <ProtectedRoute>
              <Sentry.ErrorBoundary fallback={<ErrorFallback page="tutorials" />}>
                <Tutorials />
              </Sentry.ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/shop"
          element={
            <ProtectedRoute>
              <Sentry.ErrorBoundary fallback={<ErrorFallback page="shop" />}>
                <Shop />
              </Sentry.ErrorBoundary>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-success"
          element={
            <ProtectedRoute>
              <PaymentSuccess />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-cancel"
          element={
            <ProtectedRoute>
              <PaymentCancel />
            </ProtectedRoute>
          }
        />

        {/* Admin only route */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireAdmin>
              <Admin />
            </ProtectedRoute>
          }
        />

      </Routes>
      </AnimatePresence>
      </Suspense>
  )
}

function App() {
  return (
    <HelmetProvider>
    <ThemeProvider>
      <AuthProvider>
      <BrowserRouter>
        {/* Global Toast Notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            className: 'dark:bg-zinc-900 dark:text-white dark:border-white/10 light:bg-white light:text-zinc-900 light:border-zinc-200',
            style: {
              borderRadius: '12px',
              padding: '12px 16px',
              borderWidth: '1px',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
        <ActiveAnalysisProvider>
          <UpdateAvailableModal />
          {/* Install prompt only on the canonical prod host — never on dev,
              staging, or localhost. Paired with the SW gate in main.tsx. */}
          {isPwaHost() && <PWAInstallPrompt />}
          <Sentry.ErrorBoundary fallback={<ErrorBoundary>{null}</ErrorBoundary>}>
            <ErrorBoundary>
              <AnimatedRoutes />
            </ErrorBoundary>
          </Sentry.ErrorBoundary>
        </ActiveAnalysisProvider>
      </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
  )
}

export default App
